// services/axiosCreator.ts
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import { logout } from '../slices/auth/authSlice';
import { store } from '../store/store';
import { getClientMetadataHeaders } from './clientVersion';
import { parseClientUpdateRequiredPayload } from './clientUpdateRequirement';
import { clientUpdateRequiredReceived } from '../slices/clientUpdate/clientUpdateSlice';
import {
  isAdminStepUpRequired,
  isExemptFromAuthRetry,
  isTwoFactorSetupRequired,
} from './authRetryExemptions';
import {
  ADMIN_STEP_UP_HEADER,
  clearAdminStepUpToken,
  getStoredAdminStepUpToken,
} from './adminStepUpStorage';
import { requestAdminStepUp } from './adminStepUpPrompt';
import { getBackendUrl } from '../config/backendUrl';
import {
  isLegacyExchangePending,
  runLegacyExchangeOnce,
} from './legacyExchange';
import { navigateForAuth, TWO_FACTOR_SETUP_PATH } from './authNavigation';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Never open the admin step-up prompt for this request (ORA-864): the
     * passkey ceremony calls the prompt itself makes.
     */
    skipAdminStepUpPrompt?: boolean;
    /** Set on the one replay after an admin step-up prompt. */
    _stepUpRetry?: boolean;
  }
}

export const TWO_FACTOR_SETUP_REQUIRED_MESSAGE =
  'Admins must enable two-factor authentication';

// Sessions are httpOnly Better Auth cookies on the backend origin (ORA-987):
// every request carries them, none carries a token.
const axiosInstance = axios.create({
  baseURL: getBackendUrl(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Resolve the base URL per request rather than freezing it at module load, so
// the ORA-573 backend override applies immediately after it is set (no reload)
// and so tests that stub `VITE_BACKEND_URL` still see their own value.
axiosInstance.interceptors.request.use((config) => {
  config.baseURL = getBackendUrl();
  return config;
});

axiosInstance.interceptors.request.use(
  async (config) => {
    // A browser still holding legacy tokens trades them for the session
    // cookie first; a route loader can fire before the app root starts that
    // trade, and without the cookie its request would sign the user out.
    await runLegacyExchangeOnce();

    const metadataHeaders = await getClientMetadataHeaders();
    config.headers = config.headers ?? {};

    Object.entries(metadataHeaders).forEach(([key, value]) => {
      config.headers[key] = value;
    });

    // Admin passkey step-up (ORA-864): sent whenever this device holds a
    // valid one; the backend only checks it on admin-gated routes.
    const adminStepUpToken = getStoredAdminStepUpToken();
    if (adminStepUpToken) {
      config.headers[ADMIN_STEP_UP_HEADER] = adminStepUpToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isTwoFactorSetupPromptShown = false;

/**
 * An admin without two-factor authentication is refused admin routes until
 * they enrol. Several refusals can arrive together; they share one toast and
 * one navigation.
 */
export const sendToTwoFactorSetup = () => {
  if (isTwoFactorSetupPromptShown) return;
  isTwoFactorSetupPromptShown = true;

  setTimeout(() => {
    isTwoFactorSetupPromptShown = false;
  }, 2000);

  enqueueSnackbar(TWO_FACTOR_SETUP_REQUIRED_MESSAGE, {
    variant: 'warning',
    autoHideDuration: 5000,
  });

  navigateForAuth(TWO_FACTOR_SETUP_PATH);
};

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;

    if (error.response?.status === 426) {
      const requirement = parseClientUpdateRequiredPayload(
        error.response?.data
      );

      if (requirement) {
        store.dispatch(clientUpdateRequiredReceived(requirement));
      }
      return Promise.reject(error);
    }

    // Admin passkey step-up (ORA-864): verify once, then replay this request
    // once. Requests that hit the wall together share one prompt.
    if (
      isAdminStepUpRequired(error) &&
      originalRequest &&
      !originalRequest._stepUpRetry &&
      !originalRequest.skipAdminStepUpPrompt
    ) {
      originalRequest._stepUpRetry = true;
      originalRequest.headers = originalRequest.headers ?? {};

      const sentToken = originalRequest.headers[ADMIN_STEP_UP_HEADER] ?? null;
      let stepUpToken = getStoredAdminStepUpToken();

      // A newer token than the one this request carried means the admin has
      // already re-verified (another request's prompt): just replay with it.
      if (!stepUpToken || stepUpToken === sentToken) {
        clearAdminStepUpToken();
        try {
          stepUpToken = await requestAdminStepUp(
            sentToken ? 'expired' : 'required'
          );
        } catch {
          return Promise.reject(error);
        }
      }

      originalRequest.headers[ADMIN_STEP_UP_HEADER] = stepUpToken;
      return axiosInstance(originalRequest);
    }

    if (isTwoFactorSetupRequired(error)) {
      sendToTwoFactorSetup();
      return Promise.reject(error);
    }

    // A 401 means the session cookie is gone (expired, revoked, signed out in
    // another tab). There is nothing to refresh: sign this tab out. A 403 is
    // an answer (plan refusal, admin-only route), never a session failure.
    if (
      error.response?.status === 401 &&
      !isExemptFromAuthRetry(error) &&
      store.getState().auth.isAuthenticated &&
      !store.getState().clientUpdate.requirement &&
      // Legacy tokens this boot could not trade yet: keep them (and the
      // signed-in state) for the next boot's retry.
      !isLegacyExchangePending()
    ) {
      store.dispatch(logout());
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
